import React, {forwardRef, useEffect, useState} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment, {Moment} from "moment";
import { Input } from "@rebass/forms";
import {
  useField,
  FieldMetaProps,
  FieldInputProps,
  FieldHelperProps,
} from "formik";

import {InputHelper, InputHelperOptionalParameters} from "./InputHelper";
import {StyledInputBox} from "./TextInput";


// This field takes in a string for a date, as that is how the API treats dates.
export const DateInput: React.FC<{
  name: string;
  defaultDate?: Date;
  noFutureSelect?: boolean;
  helperParameters?: InputHelperOptionalParameters;
}> = ({name, defaultDate, noFutureSelect, helperParameters= {label: name} }) => {
  const validate = (value) => {
    const newMoment = moment(value, "YYYY/MM/DD")

    if (!value) return "Select a date (year/month/day)"
    if (!newMoment.isValid()) return "Invalid date (year/month/day)";

    if ((maxDate && newMoment.isAfter(moment(maxDate)))) return "Select a date before " + moment(maxDate).format("YYYY/MM/DD")
    if (newMoment.isBefore(moment(minDate))) return "Select a date after " + moment(minDate).format("YYYY/MM/DD")
  };

  const [field, meta, helpers] = useField({ name, validate });

  const { value } = meta;
  const { setValue } = helpers;

  const parsedDate = value ? moment(value, "YYYY-MM-DD").toDate() : null;

  const maxDate = noFutureSelect === true ? new Date() : undefined
  const minDate = moment("1900-01-01", "YYYY-MM-DD").toDate()

  // Set default date to today
  useEffect(()=>{
    console.log("Date test")
    if (defaultDate) {
      setValue(defaultDate)
    }
  }, [])

  return (
    <InputHelper meta={meta} helpers={helpers} {...helperParameters}>
      <DatePicker
        maxDate={maxDate}
        minDate={minDate}
        showYearDropdown
        name={name}
        selected={parsedDate}
        onBlur={field.onBlur}
        onChange={(newDate) => {setValue(moment(newDate).toDate())}}
        customInput={
          <CustomInput
            name={name}
            value={value}
            maxDate={maxDate}
            minDate={minDate}
            setValue={(newDate) => {setValue(newDate)}}
            field={field}
            meta={meta}
            helpers={helpers}
          />
        }
        style={{
          display: "block",
        }}
      />
    </InputHelper>
  );
};

const CustomInput = forwardRef(
  (
    {value, setValue, maxDate, minDate, onClick, meta, field, helpers, name}: {
      value: string;
      setValue: (newDate: Date | null) => void;
      maxDate: Date | undefined,
      minDate: Date
      onClick?: () => {};
      onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
      meta: FieldMetaProps<any>;
      field: FieldInputProps<any>;
      helpers: FieldHelperProps<any>;
      name: string;
    },
    ref
  ) => {
    const [tempValue, setTempValue] = useState<string>( "")

    const resetTempValue = () => {
      const newMoment = moment(value, "MM/DD/YYYY")

      setTempValue(newMoment.isValid()? newMoment.format("YYYY/MM/DD") : "")
    }

    useEffect(() => {
      resetTempValue()
    }, [value])

    return (
      <StyledInputBox
        name={name}
        width={105}
        value={tempValue}
        onClick={onClick}
        onBlur={(e) => {
          resetTempValue()
          field.onBlur(e)}
        }
        onChange={(e) => {

          let newTempValue = e.target.value
          let newChar = e.nativeEvent["data"]


          const invalidCharacters = RegExp("[^\\/,[0-9]]*")
          if (invalidCharacters.test(newTempValue)) return
          if (newChar === "/") return

          // Ignore invalid years
          if (newTempValue.length > 0 && newTempValue.length <= 4) {
            const minYear = parseInt(newTempValue + new Array(4-newTempValue.length).fill("0").join(""))
            const maxYear = parseInt(newTempValue + new Array(4-newTempValue.length).fill("9").join(""))
            // @ts-ignore
            if (maxYear < minDate.getFullYear() || (maxDate && minYear > maxDate?.getFullYear())) return
          }

          // Ignore invalid months
          if (newTempValue.length === 6 && parseInt(newTempValue[5]) > 1) return
          if (newTempValue.length === 7 && parseInt(newTempValue[5] + newTempValue[6]) > 12) return

          // Ignore invalid days
          if (newTempValue.length === 9 && parseInt(newTempValue[8]) > 3) return
          if (newTempValue.length === 10 && parseInt(newTempValue[8] + newTempValue[9]) > 31) return

          if (newChar !== null && (newTempValue.length === 4 || newTempValue.length === 7)) newTempValue += '/'

          if (newChar === null && tempValue[tempValue.length - 1] === '/') newTempValue = newTempValue.slice(0, -1)

          if (newTempValue.length > 10) return

          setTempValue(newTempValue)

          const newMoment = moment(newTempValue, "YYYY/MM/DD")
          if (newTempValue.length === 10 && newMoment.isValid()){
            setValue(newMoment.toDate())
          }

          if (newTempValue.length === 0) {
            setValue(null)
          }

        }}
        placeholder="YYYY/MM/DD"
        onFocus={(event) => {
          event.target.setAttribute('autocomplete', 'off');
        }}
      />
    );
  }
);
